import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardBody, Table, Input, Badge, Button } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import AdminHeader from "./Header";
import { Users, Search, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_LIMIT = 10;

const AdminCommunityList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [list, setList] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const fetchList = useCallback(async (p = 1, q = "") => {
        dispatch(loadingTrue());
        const res = await Handler({
            method: "GET",
            url: `/communities/adminlist.php?page=${p}&limit=${PAGE_LIMIT}&q=${encodeURIComponent(q)}`,
        });
        dispatch(loadingFalse());

        if (res.success) {
            setList(res.data?.list || []);
            setTotal(res.data?.pagination?.total || 0);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchList(page, search);
    }, [page, fetchList]);

    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            fetchList(1, search);
        }, 400);
        return () => clearTimeout(t);
    }, [search, fetchList]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

    return (
        <div className="bg-soft-blue min-vh-100 pb-5">
            <AdminHeader />
            <Container py="4" className="mt-4">

                <div className="d-flex align-items-center mb-4">
                    <div className="icon-box-blue me-3 shadow-sm">
                        <Users size={22} />
                    </div>
                    <div>
                        <h3 className="fw-bold text-navy mb-0">Community Management</h3>
                        <p className="text-muted small mb-0">Oversee and moderate business communities</p>
                    </div>
                </div>

                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="py-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="input-group input-group-sm border rounded-3 overflow-hidden" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-light border-0 text-muted"><Search size={14} /></span>
                                    <Input
                                        className="bg-light border-0 shadow-none"
                                        placeholder="Search communities..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 text-md-end mt-2 mt-md-0">
                                <Badge color="light" className="text-navy border px-3 py-2 rounded-pill">
                                    Total: {total}
                                </Badge>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="table-responsive">
                        <Table hover align="middle" className="mb-0">
                            <thead className="bg-navy text-white">
                                <tr className="small text-uppercase">
                                    <th className="ps-4 py-3 border-0">#</th>
                                    <th className="border-0">Community</th>
                                    <th className="border-0">Creator</th>
                                    <th className="border-0 text-center">Members</th>
                                    <th className="border-0">Status</th>
                                    <th className="border-0 text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {list.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-5 text-muted">No data found</td></tr>
                                ) : (
                                    list.map((c, idx) => (
                                        <tr key={c.id} className="border-bottom">
                                            <td className="ps-4 small text-muted">{(page - 1) * PAGE_LIMIT + idx + 1}</td>
                                            <td>
                                                <div className="fw-bold text-navy">{c.name}</div>
                                                <Badge pill color="light" className="text-primary border font-8">{c.type}</Badge>
                                            </td>
                                            <td>
                                                <div className="small fw-semibold">{c.created_by_name || "—"}</div>
                                                <div className="text-muted x-small">{c.created_by_mobile}</div>
                                            </td>
                                            <td className="text-center fw-bold text-primary">{c.total_members}</td>
                                            <td>
                                                <span className={`badge px-2 py-1 rounded-pill ${
                                                    c.status === "ACTIVE" ? "bg-soft-success text-success" : "bg-soft-secondary text-secondary"
                                                }`}>
                                                    {c.status}
                                                </span>
                                            </td>
                                            <td className="pe-4 text-end">
                                                <button
                                                    className="btn-action-text text-navy"
                                                    onClick={() => navigate("/admin/community-members", {
                                                        state: { community_id: c.id, community_name: c.name },
                                                    })}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card>

                {total > PAGE_LIMIT && (
                    <div className="d-flex justify-content-between align-items-center mt-4 bg-white p-3 rounded-3 shadow-sm">
                        <span className="text-muted small">Page <b>{page}</b> of {totalPages}</span>
                        <div className="d-flex gap-2">
                            <Button color="outline-primary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
                                <ChevronLeft size={16} />
                            </Button>
                            <Button color="primary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                                <ChevronRight size={16} />
                            </Button>
                        </div>
                    </div>
                )}
            </Container>

            <style>{`
                .bg-soft-blue { background-color: #f0f4f8; }
                .text-navy { color: #1e3a8a; }
                .bg-navy { background-color: #1e3a8a; }
                .x-small { font-size: 0.72rem; }
                .font-8 { font-size: 0.65rem; }

                .icon-box-blue {
                    background: #1e3a8a;
                    color: white;
                    padding: 10px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                }

                .btn-action-text {
                    background: none;
                    border: none;
                    padding: 0;
                    font-size: 0.85rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: opacity 0.2s;
                }

                .btn-action-text:hover {
                    text-decoration: underline;
                    opacity: 0.8;
                }

                .bg-soft-success { background-color: #e6fffa; color: #2c7a7b; border: 1px solid #b2f5ea; }
                .bg-soft-secondary { background-color: #f7fafc; color: #4a5568; border: 1px solid #edf2f7; }

                .table thead th { font-size: 0.75rem; letter-spacing: 0.05rem; }
                .rounded-4 { border-radius: 1rem !important; }
            `}</style>
        </div>
    );
};

export default AdminCommunityList;